import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import DataLoader from "../Spinner/DataLoader";
import PageInfoModal from "../PageInfoModal/PageInfoModal";

const STATISTIKA_INFO_ITEMS = [
  { icon: 'person-fill', text: 'Sol siyahıdan bir tələbəyə klik edərək onun statistikasını yükləyə bilərsiniz.' },
  { icon: 'funnel-fill', text: 'Status filtri (Aktiv, Fasilə, Passiv…) ilə tələbə siyahısını filtirləyə bilərsiniz.' },
  { icon: 'search', text: 'Axtarış xanasına ad, soyad və ya ID yazaraq tələbəni tapa bilərsiniz.' },
  { icon: 'calendar3', text: 'Tarix aralığı seçib "Yüklə" düyməsinə klik etməklə müəyyən dövr üzrə balları görə bilərsiniz.' },
  { icon: 'graph-up', text: 'Xətti qrafik — tələbənin HW (ev tapşırığı), Test və CW (sinif işi) ballarının dərs-dərs dəyişimini göstərir.' },
  { icon: 'pie-chart-fill', text: 'Davamiyyət diaqramı — iştirak, üzürlü qayıb və üzrsüz qayıb nisbətlərini göstərir.' },
  { icon: 'bar-chart-fill', text: 'Yuxarıdakı stat kartlar — ortalama HW, Test, CW balları və ümumi davamiyyət faizini göstərir.' },
];

const COLORS = { hw: "#2563eb", test: "#3b82f6", cw: "#10b981" };
const ATTEND_COLORS = ["#059669", "#f59e0b", "#ef4444"];
const filterOptions = ["Hamısı", "Aktiv", "Fasilə", "Passiv", "Məzun"];

function avg(arr) {
  if (!arr.length) return 0;
  return Math.round((arr.reduce((s, v) => s + v, 0) / arr.length) * 10) / 10;
}

function gradeLabel(val) {
  if (val >= 8) return { text: "yüksək", color: "#059669" };
  if (val >= 5) return { text: "orta", color: "#f59e0b" };
  return { text: "aşağı", color: "#ef4444" };
}

function StatCard({ label, value, sub, color, icon, wide }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e9eaec",
      borderRadius: 10,
      padding: "13px 16px 11px",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      flex: wide ? "1.4" : "1",
      minWidth: 115,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9,
        background: `${color}15`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, color, flexShrink: 0, marginTop: 2,
      }}>
        <i className={`bi bi-${icon}`}></i>
      </div>
      <div>
        <div style={{ fontSize: 10.5, color: "#7c8494", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 21, fontWeight: 800, color: "#111827", lineHeight: 1.15 }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: sub.color, marginTop: 3, fontWeight: 500 }}>{sub.text}</div>}
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 8, padding: "9px 13px",
      boxShadow: "0 4px 14px rgba(0,0,0,0.08)", fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, color: "#374151", marginBottom: 5, fontSize: 11 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontWeight: 600, marginBottom: 2 }}>
          {p.name}: <span style={{ color: "#111827" }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function StatistikaTab({ groupId }) {
  const [activeFilter, setActiveFilter] = useState("Aktiv");
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [marks, setMarks] = useState([]);
  const [marksLoading, setMarksLoading] = useState(false);
  const [marksVersion, setMarksVersion] = useState("v2");

  const [dateFilter, setDateFilter] = useState({
    beginDate: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const fetchStudents = useCallback(async (filter) => {
    if (!groupId) return;
    setStudentsLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(`${BASE_URL}/teacher-groups/get-teacher-one-group-students`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groupId, status: filter === "Hamısı" ? "" : filter }),
        }),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      const data = await res.json();
      setStudents(data.teacherGroupStudents || []);
    } catch {
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    setSelectedStudent(null);
    setMarks([]);
    fetchStudents(activeFilter);
  }, [groupId, activeFilter, fetchStudents]);

  useEffect(() => {
    if (!selectedStudent) return;
    let alive = true;
    const fetchMarks = async () => {
      setMarksLoading(true);
      try {
        const endpoint = marksVersion === "v2"
          ? `${BASE_URL}/student-marks/mark-list/v2`
          : `${BASE_URL}/student-marks/mark-list`;
        const [res] = await Promise.all([
          apiFetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              beginDate: dateFilter.beginDate,
              endDate: dateFilter.endDate,
              contractId: selectedStudent.contractId,
            }),
          }),
          new Promise((r) => setTimeout(r, 600)),
        ]);
        const data = await res.json();
        if (alive) {
          const sorted = (data.marks || []).sort(
            (a, b) => new Date(a.teacherSelectedDate) - new Date(b.teacherSelectedDate)
          );
          setMarks(sorted);
        }
      } catch {
        if (alive) setMarks([]);
      } finally {
        if (alive) setMarksLoading(false);
      }
    };
    fetchMarks();
    return () => { alive = false; };
  }, [selectedStudent, dateFilter, refreshKey, marksVersion]);

  const filteredStudents = students.filter((s) => {
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.surname?.toLowerCase().includes(q) ||
      s.studentId?.toString().includes(q)
    );
  });

  const chartData = marks.map((m) => ({
    name: new Date(m.teacherSelectedDate).toLocaleDateString("az-AZ", { day: "2-digit", month: "short" }),
    HW: m.markHomework,
    Test: m.markTest,
    CW: m.markClasswork,
  }));

  const hwAvg   = avg(marks.map((m) => m.markHomework));
  const testAvg = avg(marks.map((m) => m.markTest));
  const cwAvg   = avg(marks.map((m) => m.markClasswork));

  const presentCount    = marks.filter((m) => m.present === true).length;
  const absentExcused   = marks.filter((m) => m.present === false && m.excusedReason === true).length;
  const absentUnexcused = marks.filter((m) => m.present === false && m.excusedReason !== true).length;
  const attendPct = marks.length ? Math.round((presentCount / marks.length) * 100) : 0;

  const pieData = [
    { name: "İştirak", value: presentCount },
    { name: "Üzürlü", value: absentExcused },
    { name: "İştirak etmədi", value: absentUnexcused },
  ].filter((d) => d.value > 0);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden", background: "#f6f7f9", position: "relative" }}>
      <DataLoader show={studentsLoading || marksLoading} />

      {/* Sol panel */}
      <div style={{
        width: 215, flexShrink: 0,
        borderRight: "1px solid #e5e7eb",
        display: "flex", flexDirection: "column",
        background: "#fff", overflow: "hidden",
      }}>
        <div style={{ padding: "11px 10px 8px", borderBottom: "1px solid #f0f1f3", display: "flex", gap: 6, alignItems: "center" }}>
          <PageInfoModal title="Statistika" items={STATISTIKA_INFO_ITEMS} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Axtar..."
            style={{
              flex: 1, minWidth: 0, border: "1px solid #e2e4e8", borderRadius: 7,
              padding: "6px 10px", fontSize: 12, outline: "none",
              color: "#374151", background: "#f9fafb",
            }}
          />
        </div>

        <div style={{ padding: "6px 8px 5px", borderBottom: "1px solid #f0f1f3", display: "flex", gap: 4, flexWrap: "wrap" }}>
          {filterOptions.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              style={{
                fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 20,
                border: "1px solid",
                borderColor: activeFilter === f ? "#2563eb" : "#e5e7eb",
                background: activeFilter === f ? "rgba(37,99,235,0.08)" : "#fff",
                color: activeFilter === f ? "#1d4ed8" : "#6b7280",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {f}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "3px 0" }}>
          {filteredStudents.length === 0 && !studentsLoading && (
            <div style={{ padding: "16px 12px", color: "#9ca3af", fontSize: 12, textAlign: "center" }}>
              Tələbə tapılmadı
            </div>
          )}
          {filteredStudents.map((st) => {
            const isSel = selectedStudent?.id === st.id;
            return (
              <div
                key={st.id}
                onClick={() => setSelectedStudent(isSel ? null : st)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: isSel ? "rgba(37,99,235,0.08)" : "transparent",
                  borderLeft: isSel ? "3px solid #2563eb" : "3px solid transparent",
                  transition: "background 0.15s",
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: isSel ? 600 : 400, color: isSel ? "#1d4ed8" : "#1f2937" }}>
                  {st.name} {st.surname}
                </div>
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>#{st.studentId}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sağ panel */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header — sabit, scroll olmur */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "9px 14px",
          borderBottom: "1px solid #e9eaec",
          background: "#fff",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", border: "1px solid #d1d5db", borderRadius: 7, overflow: "hidden", height: 28 }}>
            <button
              onClick={() => setMarksVersion("v1")}
              style={{
                padding: "0 10px", fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer",
                background: marksVersion === "v1" ? "#198754" : "#f9fafb",
                color: marksVersion === "v1" ? "#fff" : "#6b7280",
                transition: "all 0.15s",
              }}
            >
              Köhnə
            </button>
            <button
              onClick={() => setMarksVersion("v2")}
              style={{
                padding: "0 10px", fontSize: 11, fontWeight: 700, border: "none", borderLeft: "1px solid #d1d5db", cursor: "pointer",
                background: marksVersion === "v2" ? "#198754" : "#f9fafb",
                color: marksVersion === "v2" ? "#fff" : "#6b7280",
                transition: "all 0.15s",
              }}
            >
              Yeni
            </button>
          </div>
          <input
            type="date"
            value={dateFilter.beginDate}
            min={marksVersion === "v2" ? "2026-04-01" : undefined}
            onChange={(e) => setDateFilter((p) => ({ ...p, beginDate: e.target.value }))}
            style={{ border: "1px solid #e2e4e8", borderRadius: 6, padding: "4px 8px", fontSize: 11.5, color: "#374151", outline: "none" }}
          />
          <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
          <input
            type="date"
            value={dateFilter.endDate}
            max={marksVersion === "v1" ? "2026-04-30" : undefined}
            onChange={(e) => setDateFilter((p) => ({ ...p, endDate: e.target.value }))}
            style={{ border: "1px solid #e2e4e8", borderRadius: 6, padding: "4px 8px", fontSize: 11.5, color: "#374151", outline: "none" }}
          />
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            title="Yenilə"
            style={{
              width: 28, height: 28,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid #e5e7eb", borderRadius: 4,
              background: "#fff", color: "#2563eb",
              cursor: "pointer", fontSize: 13,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            <i
              className="bi bi-arrow-clockwise"
              style={{ animation: marksLoading ? "spin 0.7s linear infinite" : "none" }}
            />
          </button>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

        {/* Kontent — scroll olur */}
        <div style={{ flex: 1, overflow: "auto", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

        {!selectedStudent ? (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            color: "#b0b7c3", gap: 10, paddingBottom: 60,
          }}>
            <i className="bi bi-person-lines-fill" style={{ fontSize: 40, color: "#d8dce5" }}></i>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#9ca3af" }}>Tələbə seçin</div>
            <div style={{ fontSize: 11, color: "#b0b7c3" }}>Sol paneldən tələbəni seçərək statistikasını görün</div>
          </div>
        ) : (
          <>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                {selectedStudent.name} {selectedStudent.surname}
              </div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 2 }}>
                #{selectedStudent.studentId} &nbsp;·&nbsp; {marks.length} dərs qeydi
              </div>
            </div>

            {marks.length === 0 && !marksLoading ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 13 }}>
                <i className="bi bi-inbox" style={{ fontSize: 34, display: "block", marginBottom: 8, color: "#d1d5db" }}></i>
                Bu tarix aralığında məlumat tapılmadı
              </div>
            ) : (
              <>
                {/* Stat kartlar */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <StatCard label="Ev tapşırığı" value={hwAvg}       sub={gradeLabel(hwAvg)}   color={COLORS.hw}   icon="house-fill"           />
                  <StatCard label="Test"          value={testAvg}     sub={gradeLabel(testAvg)} color={COLORS.test} icon="clipboard-check-fill"  />
                  <StatCard label="Sinif işi"     value={cwAvg}       sub={gradeLabel(cwAvg)}   color={COLORS.cw}   icon="pencil-fill"           />
                  <StatCard label="Davamiyyət"    value={`${attendPct}%`} sub={{ text: `${presentCount} / ${marks.length} dərs`, color: "#6b7280" }} color="#f59e0b" icon="calendar2-check-fill" wide />
                </div>

                {/* Qrafiklər */}
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

                  {/* Line chart */}
                  <div style={{
                    flex: 2, minWidth: 280,
                    background: "#fff", border: "1px solid #e9eaec",
                    borderRadius: 10, padding: "15px 14px 10px",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 12, letterSpacing: "-0.1px" }}>
                      Qiymət dinamikası
                    </div>
                    <ResponsiveContainer width="100%" height={210}>
                      <LineChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 4" stroke="#f3f4f6" />
                        <XAxis dataKey="name" tick={{ fontSize: 9.5, fill: "#9ca3af" }} />
                        <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 9.5, fill: "#9ca3af" }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 10.5, paddingTop: 6 }} />
                        <Line type="monotone" dataKey="HW"   name="Ev tapşırığı" stroke={COLORS.hw}   strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: COLORS.hw }}   activeDot={{ r: 4 }} />
                        <Line type="monotone" dataKey="Test" name="Test"          stroke={COLORS.test} strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: COLORS.test }} activeDot={{ r: 4 }} />
                        <Line type="monotone" dataKey="CW"   name="Sinif işi"     stroke={COLORS.cw}   strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: COLORS.cw }}   activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Donut */}
                  <div style={{
                    flex: 1, minWidth: 195,
                    background: "#fff", border: "1px solid #e9eaec",
                    borderRadius: 10, padding: "15px 14px 12px",
                    display: "flex", flexDirection: "column",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 10, letterSpacing: "-0.1px" }}>
                      Davamiyyət bölgüsü
                    </div>
                    {pieData.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={150}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%" cy="50%"
                              innerRadius={46} outerRadius={68}
                              paddingAngle={3} dataKey="value"
                            >
                              {pieData.map((_, i) => <Cell key={i} fill={ATTEND_COLORS[i]} />)}
                            </Pie>
                            <Tooltip formatter={(v, n) => [`${v} dərs`, n]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 5 }}>
                          {pieData.map((d, i) => (
                            <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11 }}>
                              <span style={{ width: 9, height: 9, borderRadius: "50%", background: ATTEND_COLORS[i], flexShrink: 0, display: "inline-block" }} />
                              <span style={{ color: "#6b7280", flex: 1 }}>{d.name}</span>
                              <span style={{ fontWeight: 600, color: "#374151" }}>{d.value}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#d1d5db", fontSize: 12 }}>
                        Məlumat yoxdur
                      </div>
                    )}
                  </div>

                </div>
              </>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
